import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, useToast } from '@guestyci/arc';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';
import { okta } from 'utils';
import t from '@guestyci/localize/t.macro';

import { LoginPage, ForgotPasswordPage, LogoutPage } from './pages';
import LoginCallback from '../components/LoginCallback/LoginCallback';
import LoginSpinner from '../app/LoginSpinner';
import { HelpCenterSSOForm } from './components';
import Impersonation from '../impersonation/Impersonation';
import SessionHandler from '../components/SessionHandler/SessionHandler';
import GoogleLoginHandler from '../components/GoogleLoginHandler';
import {
  redirectToHqIfNeeded,
  completeLoginAndRedirect,
  trackSuccessfulLogin,
  extractAndPersistUserData,
  enrichUserData,
  getZendeskRedirectUrlFromSessionStorage,
  submitZendeskForm,
} from './services';
import {
  getSecureAuthCookie,
  setAuthDesignDDContextEnabled,
} from '../login/helpers';
import { saveUserPreferences } from 'utils/userPreferences';

const oktaAuth = new OktaAuth({ ...okta.oidc });

const handleTokenReceived = async () => {
  if (!oktaAuth) return;

  try {
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    trackSuccessfulLogin(accessToken);
    extractAndPersistUserData(accessToken);
  } catch (error) {
    console.error('Error handling token received:', error);
  }
};

const App = () => {
  const [error, setError] = useState(null);
  const { toast } = useToast();
  const history = useHistory();

  const zendeskFormRef = useRef(null);
  const zendeskInputRef = useRef(null);

  const onAuthResume = useCallback(() => {
    history.push('/login');
  }, [history]);

  const handleRestoreOriginalUri = useCallback(async () => {
    if (!oktaAuth) {
      setError('OktaAuth not initialized');
      return;
    }

    await handleTokenReceived();
    try {
      await getSecureAuthCookie();
    } catch (e) {
      console.error(e);
    }

    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken');

      if (!accessToken?.accessToken) {
        console.error('Authentication token missing or invalid after login');
        setError(t('Authentication token missing — please sign in again'));
        history.push('/login');
        return;
      }

      const zendeskRedirectUrl = getZendeskRedirectUrlFromSessionStorage();
      if (zendeskRedirectUrl) {
        const result = await submitZendeskForm({
          accessToken,
          zendeskRedirectUrl,
          formRef: zendeskFormRef,
          inputRef: zendeskInputRef,
        });
        if (!result.success) {
          setError(result.error);
          history.push('/login');
        }
        return;
      }

      const user = await enrichUserData();
      await saveUserPreferences({
        accessToken: accessToken?.accessToken,
        preferences: user?.preferences,
      });

      if (redirectToHqIfNeeded(accessToken)) {
        return;
      }

      completeLoginAndRedirect();
    } catch (error) {
      console.error('Error in post-auth redirect:', error);
      setError(t('Login completed but redirect failed. Please try again.'));
      history.push('/login');
    }
  }, [history]);

  useEffect(() => {
    if (error) {
      toast({ variant: 'destructive', description: error });
    }
  }, [error, toast]);

  useEffect(() => {
    setAuthDesignDDContextEnabled();
  }, []);

  return (
    <>
      <Security
        oktaAuth={oktaAuth}
        restoreOriginalUri={handleRestoreOriginalUri}
      >
        <Container
          height="auto"
          maxWidth="full"
          gutter={0}
          variant="default"
          className="gst-bg-white gst-min-h-screen gst-flex gst-flex-col"
          style={{ overflowX: 'clip' }}
        >
          <Switch>
            <Route exact path="/login" component={LoginPage} />
            <Route
              path="/login/callback"
              render={props => (
                <LoginCallback
                  {...props}
                  oktaAuth={oktaAuth}
                  authState={oktaAuth.authStateManager.getAuthState()}
                  loadingElement={<LoginSpinner />}
                  onAuthResume={onAuthResume}
                />
              )}
            />
            <Route exact path="/forgot" component={ForgotPasswordPage} />
            <Route exact path="/logout" component={LogoutPage} />
            <Route exact path="/impersonate" component={Impersonation} />
            <Route exact path="/instance-login" component={SessionHandler} />
            <Route exact path="/session-handler" component={SessionHandler} />
            <Route exact path="/google-login" component={GoogleLoginHandler} />
            <Redirect to="/login" />
          </Switch>
        </Container>
      </Security>
      <HelpCenterSSOForm ref={zendeskFormRef} inputRef={zendeskInputRef} />
    </>
  );
};

export default App;
